import { useState, memo, useMemo, useEffect, useRef } from "react";
import { useFormikContext, FieldArray } from "formik";
import { useDispatch } from "react-redux";
import { message, Tooltip, Modal } from "antd";
import socket from "./socket";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane, faEnvelope, faChevronDown, faChevronRight,
  faCircleCheck, faClock, faCircleXmark, faEye, faStop,
  faArrowRight, faPlay, faInbox, faCheckDouble, faReply,
  faBell, faShare, faRotate, faBullhorn,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import axios from "axios";
import { SERVER_URL } from "../../../databucket";
import { FetchReplysThunk } from "../../../Slices/Thunk/notification";
import { timerHistoryPayloadChange } from "../../../Slices/user";
import { Starttask } from "../../../Slices/timerslice";
import { updateWorkHistoryThunk } from "../../../Slices/orderSlice";
import { settaskStartTime } from "../../../Slices/user";
import { setRepliesForEmail, addReplyNotification } from "../../../Slices/emailRepliesSlice";
import CommentField from "./CommentField";
import EmailForm from "./EmailForm";
import { setLoader } from "../../../Slices/loaderSlice";
import EmailThread from "./EmailThread";
import './Frames.scss';
import { getAllLeadWorkHistory, discardDraftThunk } from "../../../Slices/salesSlice";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

// ─── constants ────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  pending:      { color: "#f59e0b", bg: "#fffbeb", label: "Pending",           icon: faClock,        border: "#fcd34d" },
  delivered:    { color: "#10b981", bg: "#ecfdf5", label: "Delivered",         icon: faCircleCheck,  border: "#6ee7b7" },
  opened:       { color: "#3b82f6", bg: "#eff6ff", label: "Opened",            icon: faEye,          border: "#93c5fd" },
  replied:      { color: "#8b5cf6", bg: "#f5f3ff", label: "Response Received", icon: faReply,        border: "#c4b5fd" },
  bounced:      { color: "#ef4444", bg: "#fef2f2", label: "Hard Bounce",       icon: faCircleXmark,  border: "#fca5a5" },
  soft_bounced: { color: "#f97316", bg: "#fff7ed", label: "Soft Bounce",       icon: faCircleXmark,  border: "#fdba74" },
  complained:   { color: "#dc2626", bg: "#fef2f2", label: "Spam Complaint",    icon: faStop,         border: "#f87171" },
  rejected:     { color: "#7c3aed", bg: "#f5f3ff", label: "Rejected",          icon: faCircleXmark,  border: "#a78bfa" },
};

const AVATAR_COLORS = ["#6366f1","#8b5cf6","#ec4899","#f97316","#14b8a6","#06b6d4","#f59e0b","#10b981"];

const getAvatarColor = (str = "") =>
  AVATAR_COLORS[(str || "").charCodeAt(0) % AVATAR_COLORS.length];

const shortEmail = (email = "") => {
  if (!email) return "";
  const [name, domain] = email.split("@");
  if (!name) return email;
  const shortName = name.length > 10 ? name.slice(0, 10) + "…" : name;
  return domain ? `${shortName}@${domain.split(".")[0]}` : shortName;
};

// ─── sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, color, bg }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 12,
    background: bg, borderRadius: 12, padding: "12px 18px",
    flex: "1 1 0", minWidth: 0, border: `1.5px solid ${color}33`,
    boxShadow: `0 2px 8px ${color}18`,
  }}>
    <div style={{
      width: 38, height: 38, borderRadius: 10,
      background: `${color}22`, display: "flex",
      alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <FontAwesomeIcon icon={icon} style={{ color, fontSize: 16 }} />
    </div>
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500, marginTop: 2 }}>{label}</div>
    </div>
  </div>
);

const DeliveryBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 11,
      fontWeight: 700, color: cfg.color, background: cfg.bg,
      border: `1.5px solid ${cfg.border}`, letterSpacing: "0.2px",
    }}>
      <FontAwesomeIcon icon={cfg.icon} style={{ fontSize: 10 }} />
      {cfg.label}
    </span>
  );
};

const Avatar = ({ email, size = 34 }) => {
  const color = getAvatarColor(email);
  const letter = (email || "?").charAt(0).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `linear-gradient(135deg, ${color}, ${color}bb)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", fontWeight: 700, fontSize: size * 0.38, flexShrink: 0,
      boxShadow: `0 2px 6px ${color}55`,
    }}>
      {letter}
    </div>
  );
};

const EmptyEmails = () => (
  <div style={{
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "48px 24px", gap: 12,
  }}>
    <div style={{
      width: 72, height: 72, borderRadius: "50%",
      background: "linear-gradient(135deg, #e0e7ff, #c7d2fe)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 16px #6366f133",
    }}>
      <FontAwesomeIcon icon={faInbox} style={{ fontSize: 30, color: "#6366f1" }} />
    </div>
    <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>No emails yet</div>
    <div style={{ fontSize: 13, color: "#94a3b8", textAlign: "center", maxWidth: 260 }}>
      Send the first email to this lead using the <strong>New Mail</strong> button above.
    </div>
  </div>
);

// ─── main component ───────────────────────────────────────────────────────────

const EmailTrack = memo((props) => {
  const CurrentTask = "Email";
  const dispatch = useDispatch();
  const { SalesInfo, setFieldValue, id, leadID } = props;
  const { values, submitForm } = useFormikContext();
  // Use Formik values for leadsContacts so opt-in changes reflect immediately
  // without waiting for a backend save + Redux update cycle.
  const effectiveSalesInfo = values?.leadsContacts
    ? { ...SalesInfo, leadsContacts: values.leadsContacts }
    : SalesInfo;
  const task = useSelector((state) => state.timer.task);
  const salesLeadId = leadID;
  const WorkingId = useSelector((state) => state?.timer?.requestId);
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const loginUserName = loginUser.firstname + " " + loginUser.lastname;
  const repliesMap = useSelector((state) => state.emailReplies.repliesByEmailId);
  const sharedEmails = useSelector((state) => state?.loginUserInfo?.loginUserDetails?.sharedemailSettings);
  const sharedemails = useSelector((state) => state?.sharedEmail?.sharedemails);

  const emailMap = sharedemails?.reduce((acc, item) => { acc[item._id] = item.email; return acc; }, {}) || {};
  const sharedMails = sharedEmails?.filter(i => i.status === true || i.status === "true")?.map(i => emailMap[i.email])?.filter(Boolean) || [];
  const privateEmails = loginUser?.emailSettings?.filter(i => i.status === true || i.status === "true")?.map(i => i.email) || [];
  const salesEmails = loginUser?.salesEmailSettings?.filter(i => i.status === true || i.status === "true")?.map(i => i.email)?.filter(Boolean) || [];
  const EmailList = Array.from(new Set([...privateEmails, ...sharedMails, ...salesEmails])).map((item, index) => ({ key: index, value: item, label: item }));

  const [emailOpen, setEmailOpen] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null); // null = new compose, or draft entry object
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [replyCompose, setReplyCompose] = useState(null); // null | { mode, data }
  const [loadingReplies, setLoadingReplies] = useState({});
  const [newReplyIds, setNewReplyIds] = useState(new Set()); // emailIDs that have new replies
  const [unreadInboxCount, setUnreadInboxCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  // Ref so socket handlers always have the latest leadsEmails without stale closures
  const leadsEmailsRef = useRef(values.leadsEmails);
  useEffect(() => { leadsEmailsRef.current = values.leadsEmails; }, [values.leadsEmails]);

  // ── Sync SesTracking status on mount (same approach as Outlook module) ──
  // Tracking pixels fire to the remote server; this pulls live status from SesTracking
  // so the badge is always correct without needing a real-time socket event.
  useEffect(() => {
    const emails = values.leadsEmails || [];
    const trackIds = emails.map(e => e.trackId).filter(Boolean);
    if (trackIds.length === 0) return;
    axios.post(`${SERVER_URL}/api/ses/status-sync`, { trackIds })
      .then(({ data }) => {
        const statuses = data?.statuses || {};
        if (Object.keys(statuses).length === 0) return;
        const priority = { sent: 0, pending: 0, delivered: 1, opened: 2, clicked: 2, soft_bounced: 2, replied: 3, bounced: 4, complained: 4, rejected: 4 };
        const current = leadsEmailsRef.current || [];
        let changed = false;
        const updated = current.map(e => {
          const live = statuses[e.trackId];
          if (!live) return e;
          const curPri = priority[e.deliveryStatus] ?? 0;
          const livePri = priority[live] ?? 0;
          if (livePri > curPri) { changed = true; return { ...e, deliveryStatus: live }; }
          return e;
        });
        if (changed) setFieldValue('leadsEmails', updated);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadID]);

  // ── Auto-fetch threads for replied emails on lead load ──
  // The replyPollerCron already saves deliveryStatus:'replied' to the DB.
  // When the user opens a lead, the data already reflects that — this effect
  // auto-fetches and expands those threads so the user NEVER has to manually
  // click "View thread" to discover a reply.
  useEffect(() => {
    const emails = values.leadsEmails || [];
    const repliedEmails = emails.filter(e =>
      e.deliveryStatus === 'replied' && e.emailID && e.emailFrom
    );
    if (repliedEmails.length === 0) return;
    const timer = setTimeout(() => {
      repliedEmails.forEach(email => {
        fetchReplies(email.emailID, email.emailFrom, email.replyMessageIds || [], true); // silent=true: no toast on mount
        setExpandedRowKeys(prev =>
          prev.includes(email.emailID) ? prev : [...prev, email.emailID]
        );
      });
    }, 800);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadID]);

  const isTimerRunning = WorkingId === leadID && task === CurrentTask;

  // ── Socket.IO: real-time email:opened push from backend tracking pixel ──
  useEffect(() => {
    const handleTrackOpened = ({ trackId, leadId }) => {
      // Only update if this event belongs to the current lead
      if (!leadId || !trackId) return;
      const emails = leadsEmailsRef.current || [];
      const match = emails.find(e => e.trackId === trackId);
      if (!match) return;
      // Update Formik in-place without triggering a form submit
      const updated = emails.map(e =>
        e.trackId === trackId ? { ...e, deliveryStatus: 'opened' } : e
      );
      setFieldValue('leadsEmails', updated);
      message.info({
        content: `📬 Your email to ${match.emailedto || 'recipient'} was opened!`,
        key: `track-${trackId}`,
        duration: 5,
      });
    };

    // New inbox email notification (emitted from fetchInitialEmails polling)
    const handleNewInboxEmail = ({ count }) => {
      if (count > 0) setUnreadInboxCount(prev => prev + count);
    };

    // SES webhook real-time events: delivery, bounce, open, click, replied
    const handleSesEvent = ({ messageId, status, bounceReason, from, subject, leadId: eventLeadId }) => {
      if (!messageId || !status) return;
      const emails  = leadsEmailsRef.current || [];
      const shortId = messageId.split('@')[0];
      const match   = emails.find(e => e.emailID === messageId || e.emailID === shortId);

      // ── Status priority guard — never downgrade, never overwrite replied ────
      const PRIORITY = { sent: 0, pending: 0, delivered: 1, opened: 2, clicked: 2, soft_bounced: 2, replied: 3, bounced: 4, complained: 4, rejected: 4 };
      if (match) {
        // 'replied' is final — no status event can undo a received reply
        if (match.deliveryStatus === 'replied') return;
        const curPri = PRIORITY[match.deliveryStatus] ?? 0;
        const newPri = PRIORITY[status] ?? 0;
        if (newPri >= curPri) {
          const updated = emails.map(e => {
            if (e.emailID !== messageId && e.emailID !== shortId) return e;
            const update = { ...e, deliveryStatus: status };
            if (bounceReason) update.bounceReason = bounceReason;
            if (status === 'opened')  update.openedAt = new Date().toISOString();
            return update;
          });
          setFieldValue('leadsEmails', updated);
        }
      }

      // ── Reply notification — fire even if user is on a different lead ────
      if (status === 'replied') {
        dispatch(addReplyNotification({
          leadId:    eventLeadId || leadID,
          messageId,
          from:      from || '',
          subject:   subject || match?.emailBody || '',
          receivedAt: new Date().toISOString(),
        }));
        // Auto-fetch thread so the reply appears without user needing to click
        if (match?.emailID && match?.emailFrom) {
          setTimeout(() => {
            fetchReplies(match.emailID, match.emailFrom, match.replyMessageIds || []);
            setExpandedRowKeys(prev =>
              prev.includes(match.emailID) ? prev : [...prev, match.emailID]
            );
          }, 1500);
        }
      }

      const labels = { delivered: 'delivered', opened: '📬 opened', replied: '💬 reply received', bounced: '❌ bounced' };
      if (labels[status]) {
        message.info({ content: `Email ${labels[status]}`, key: `ses-${messageId}`, duration: 4 });
      }
    };

    socket.on('email:trackOpened', handleTrackOpened);
    socket.on('email:newInboxLocal', handleNewInboxEmail);
    socket.on('email:sesEvent', handleSesEvent);
    return () => {
      socket.off('email:trackOpened', handleTrackOpened);
      socket.off('email:newInboxLocal', handleNewInboxEmail);
      socket.off('email:sesEvent', handleSesEvent);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFieldValue]);

  const stats = useMemo(() => {
    const emails = values.leadsEmails || [];
    return {
      total:       emails.length,
      replied:     emails.filter(e => e.deliveryStatus === "replied").length,
      opened:      emails.filter(e => e.deliveryStatus === "opened").length,
      pending:     emails.filter(e => !e.deliveryStatus || e.deliveryStatus === "pending").length,
      bounced:     emails.filter(e => ["bounced", "soft_bounced", "complained", "rejected"].includes(e.deliveryStatus)).length,
      delivered:   emails.filter(e => e.deliveryStatus === "delivered").length,
    };
  }, [values.leadsEmails]);

  // ── business logic (unchanged) ──────────────────────────────────────────────

  const handleStartButtonClick = async () => {
    const payload = { department: "Sales", task: "Email", time: Date.now(), requestId: SalesInfo?.leadID || salesLeadId };
    try {
      await dispatch(updateWorkHistoryThunk({
        userId: loginUser.id, emailId: SalesInfo?.id || "",
        requestId: SalesInfo?.leadID?.toString() || salesLeadId,
        clientName: "Blueplanit", processType: "", taskType: "Email",
        email: "", subject: "", body: "",
        startTime: Date.now().toString(), endTime: "", department: "Sales",
      })).unwrap();
    } catch (err) { console.error(err); }
    localStorage.setItem("task", "Email");
    localStorage.setItem("time", Date.now());
    localStorage.setItem("department", "sales");
    localStorage.setItem("requestId", SalesInfo?.leadID || salesLeadId);
    dispatch(Starttask(payload));
  };

  const handleStop = async () => {
    dispatch(timerHistoryPayloadChange({ data: { id: Math.random().toString(), userId: loginUser.id, title: "", description: "", clientName: "Blueplanit", department: loginUser.department, taskType: "Available", startTime: Date.now().toString(), endTime: Date.now().toString(), requestId: "" } }));
    await dispatch(updateWorkHistoryThunk({ userId: loginUser.id, emailId: "", requestId: "", clientName: "Blueplanit", processType: "", taskType: "Available", email: "", subject: "", body: "", startTime: Date.now().toString(), endTime: "", department: loginUser.department })).unwrap();
    localStorage.setItem("time", new Date().getTime());
    localStorage.setItem("task", "Available");
    localStorage.removeItem("department");
    localStorage.removeItem("requestId");
    dispatch(settaskStartTime(new Date().getTime()));
    dispatch(Starttask({ department: "", requestId: "", task: "Available", time: Date.now() }));
    dispatch(getAllLeadWorkHistory(SalesInfo?.id));
  };

  const fetchReplies = async (internetMessageId, email, replyMessageIds, silent = false) => {
    if (!internetMessageId || !email) { message.warning("From email is required to fetch replies."); return; }
    setLoadingReplies(prev => ({ ...prev, [internetMessageId]: true }));
    dispatch(setLoader(true));
    try {
      const res = await dispatch(FetchReplysThunk({ email, messageId: internetMessageId, replyMessageIds })).unwrap();
      const fetchedReplies = res?.replies || [];
      if (fetchedReplies.length > 0) {
        dispatch(setRepliesForEmail({ emailID: internetMessageId, replies: fetchedReplies }));
        // Only count actual replies from the prospect (not BD's own sent messages)
        const isOwnSent = r => r.provider === 'ses-sent' || r.provider === 'ses-sent-reply';
        const hasActualReply = fetchedReplies.some(r => !isOwnSent(r));
        if (hasActualReply) {
          const replyCount = fetchedReplies.filter(r => !isOwnSent(r)).length;
          // Only toast if this is a newly discovered reply — not if we already knew about it
          const currentEmail = (leadsEmailsRef.current || []).find(e => e.emailID === internetMessageId);
          const wasAlreadyReplied = currentEmail?.deliveryStatus === 'replied';
          if (!silent && !wasAlreadyReplied) {
            message.success(`${replyCount} repl${replyCount === 1 ? 'y' : 'ies'} received!`);
          }
          // Mark the email as "replied" in Formik state — use ref to avoid stale async closure
          const emails = leadsEmailsRef.current || [];
          const updated = emails.map(e =>
            e.emailID === internetMessageId
              ? { ...e, deliveryStatus: 'replied' }
              : e
          );
          setFieldValue('leadsEmails', updated);
          setNewReplyIds(prev => new Set([...prev, internetMessageId]));
        } else {
          // Sync badge status from SesTracking (handles pixel firing to remote server in local dev)
          const sesSentMsg = fetchedReplies.find(r => r.provider === 'ses-sent');
          if (sesSentMsg?.sesStatus) {
            const priority = { sent: 0, pending: 0, delivered: 1, opened: 2, clicked: 2, soft_bounced: 2, replied: 3, bounced: 4, complained: 4, rejected: 4 };
            const emails = leadsEmailsRef.current || [];
            const currentEmail = emails.find(e => e.emailID === internetMessageId);
            const curPri = priority[currentEmail?.deliveryStatus] ?? 0;
            const newPri = priority[sesSentMsg.sesStatus] ?? 0;
            if (newPri > curPri) {
              const updated = emails.map(e =>
                e.emailID === internetMessageId
                  ? { ...e, deliveryStatus: sesSentMsg.sesStatus }
                  : e
              );
              setFieldValue('leadsEmails', updated);
            }
          }
        }
      } else {
        message.info("No replies found for this thread.");
      }
    } catch (err) { console.warn(err.message); }
    finally {
      dispatch(setLoader(false));
      setLoadingReplies(prev => ({ ...prev, [internetMessageId]: false }));
    }
  };

  const handleRefresh = async () => {
    const emails = values.leadsEmails || [];
    const trackable = emails.filter(e => e.emailID && e.emailFrom);
    if (trackable.length === 0) { message.info("No tracked emails to refresh."); return; }
    setRefreshing(true);
    try {
      await Promise.all(
        trackable.map(e => fetchReplies(e.emailID, e.emailFrom, e.replyMessageIds || [], true))
      );
      message.success("Emails refreshed.");
    } finally {
      setRefreshing(false);
    }
  };

  const onExpand = async (key, record) => {
    if (expandedRowKeys.includes(key)) {
      setExpandedRowKeys(prev => prev.filter(k => k !== key));
    } else {
      await fetchReplies(record.emailID, record.emailFrom, record.replyMessageIds);
      setExpandedRowKeys(prev => [...prev, key]);
    }
  };

  // Split drafts (pinned at top) from sent emails (shown in reverse chronological order)
  const draftEmails = useMemo(
    () => (values.leadsEmails || []).filter(e => e.isDraft),
    [values.leadsEmails]
  );
  const reversed = useMemo(
    () => [...(values.leadsEmails || []).filter(e => !e.isDraft)].reverse(),
    [values.leadsEmails]
  );

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ marginTop: 16, borderRadius: 14, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.09)", border: "1px solid #e8edf5" }}>

      {/* ── Header ── */}
      <div style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)",
        padding: "18px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 12px #3b82f655",
          }}>
            <FontAwesomeIcon icon={faBullhorn} style={{ color: "#fff", fontSize: 16 }} />
          </div>
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 16, lineHeight: 1.2 }}>Email Outreach</div>
            <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2 }}>
              {stats.total} email{stats.total !== 1 ? "s" : ""} tracked
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {id !== undefined && (
            isTimerRunning ? (
              <button type="button" onClick={handleStop} style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
                color: "#fff", fontWeight: 700, fontSize: 13,
                boxShadow: "0 2px 8px #ef444455",
              }}>
                <FontAwesomeIcon icon={faStop} style={{ fontSize: 11 }} />
                Stop Timer
              </button>
            ) : (
              <button type="button" onClick={handleStartButtonClick} style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #10b981, #059669)",
                color: "#fff", fontWeight: 700, fontSize: 13,
                boxShadow: "0 2px 8px #10b98155",
              }}>
                <FontAwesomeIcon icon={faPlay} style={{ fontSize: 11 }} />
                Start Timer
              </button>
            )
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)",
              cursor: refreshing ? "default" : "pointer",
              background: "rgba(255,255,255,0.08)",
              color: "#fff", fontWeight: 600, fontSize: 13,
              opacity: refreshing ? 0.7 : 1,
              transition: "all 0.2s",
            }}
          >
            <FontAwesomeIcon icon={faRotate} spin={refreshing} style={{ fontSize: 13 }} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            disabled={leadID === undefined}
            onClick={() => setEmailOpen(!emailOpen)}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "8px 18px", borderRadius: 8, border: "none",
              cursor: leadID === undefined ? "not-allowed" : "pointer",
              background: emailOpen
                ? "rgba(255,255,255,0.15)"
                : "linear-gradient(135deg, #6366f1, #8b5cf6)",
              color: "#fff", fontWeight: 700, fontSize: 13,
              opacity: leadID === undefined ? 0.5 : 1,
              boxShadow: emailOpen ? "none" : "0 2px 10px #6366f155",
              transition: "all 0.2s",
            }}
          >
            <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 13 }} />
            {emailOpen ? "Close" : "New Mail"}
          </button>
        </div>
      </div>

      {/* ── Unread inbox notification banner ── */}
      {unreadInboxCount > 0 && (
        <div style={{
          background: "linear-gradient(135deg, #8b5cf6, #6366f1)",
          padding: "8px 24px",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <FontAwesomeIcon icon={faBell} style={{ color: "#fff", fontSize: 13 }} />
          <span style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>
            {unreadInboxCount} new email{unreadInboxCount !== 1 ? "s" : ""} in your inbox
          </span>
          <button
            onClick={() => setUnreadInboxCount(0)}
            style={{
              marginLeft: "auto", background: "rgba(255,255,255,0.2)",
              border: "none", color: "#fff", borderRadius: 6,
              padding: "2px 10px", fontSize: 12, cursor: "pointer", fontWeight: 600,
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Reply received notification banner ── */}
      {stats.replied > 0 && (
        <div style={{
          background: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
          borderBottom: "1px solid #c4b5fd",
          padding: "9px 24px",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%",
            background: "linear-gradient(135deg, #7c3aed, #6366f1)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <FontAwesomeIcon icon={faReply} style={{ color: "#fff", fontSize: 11 }} />
          </div>
          <div>
            <span style={{ color: "#5b21b6", fontSize: 13, fontWeight: 700 }}>
              {stats.replied} repl{stats.replied === 1 ? 'y' : 'ies'} received
            </span>
            <span style={{ color: "#7c3aed", fontSize: 12, marginLeft: 8 }}>
              — thread{stats.replied === 1 ? '' : 's'} auto-expanded below
            </span>
          </div>
        </div>
      )}

      {/* ── Stats Bar ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 6, padding: "8px 20px",
        background: "#f8fafc", borderBottom: "1px solid #e2e8f0", flexWrap: "wrap",
      }}>
        {[
          { icon: faPaperPlane,  label: "Sent",      value: stats.total,     color: "#6366f1" },
          { icon: faReply,       label: "Responses", value: stats.replied,   color: "#8b5cf6" },
          { icon: faEye,         label: "Opened",    value: stats.opened,    color: "#3b82f6" },
          { icon: faCheckDouble, label: "Delivered", value: stats.delivered, color: "#10b981" },
          { icon: faClock,       label: "Pending",   value: stats.pending,   color: "#f59e0b" },
          { icon: faCircleXmark, label: "Bounced",   value: stats.bounced,   color: "#ef4444" },
        ].map(({ icon, label, value, color }) => (
          <div key={label} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: "#fff", border: `1px solid ${color}33`,
            borderRadius: 20, padding: "3px 10px 3px 7px",
            fontSize: 12, color: "#334155", boxShadow: `0 1px 3px ${color}18`,
          }}>
            <FontAwesomeIcon icon={icon} style={{ color, fontSize: 11 }} />
            <span style={{ fontWeight: 700, color }}>{value}</span>
            <span style={{ color: "#64748b" }}>{label}</span>
          </div>
        ))}
      </div>

      {/* ── Body ── */}
      <div style={{ background: "#fff", padding: "0 20px 20px" }}>
        <FieldArray
          name="leadsEmails"
          render={() => (
            <>
              {/* Email compose form */}
              {emailOpen && (
                <div style={{
                  margin: "16px 0",
                  borderRadius: 12,
                  border: "1.5px solid #c7d2fe",
                  background: "#f8faff",
                  overflow: "hidden",
                  boxShadow: "0 4px 20px #6366f118",
                }}>
                  <EmailForm
                    salesInfo={effectiveSalesInfo}
                    draftData={editingDraft}
                    onDraftSaved={(updatedEmails) => {
                      // Merge updated leadsEmails (contains the saved draft) into Formik
                      if (updatedEmails?.length) setFieldValue("leadsEmails", updatedEmails);
                    }}
                    onDraftDiscarded={(draftId, updatedEmails) => {
                      if (updatedEmails?.length) {
                        setFieldValue("leadsEmails", updatedEmails);
                      } else {
                        // Optimistic remove
                        setFieldValue("leadsEmails", (values.leadsEmails || []).filter(e => e.id !== draftId));
                      }
                      setEditingDraft(null);
                      setEmailOpen(false);
                    }}
                    onSuccess={async (emailData) => {
                      const newDate = dayjs();
                      const sendStatus = emailData?.sendStatus || [];
                      const newEmails = sendStatus.map((status) => ({
                        date: status.sentDateTime || newDate,
                        time: dayjs().set("second", 0).set("millisecond", 0),
                        status: status.status === "failed" ? "Failed" : "Sent",
                        emailedto: status.email || "",
                        emailedy: loginUserName,
                        remarks: "",
                        emailFrom: status.from || "",
                        emailFollowup: "FollowUp",
                        emailBody: status.subject || "",
                        emailID: status.messageId || "",
                        trackId: status.trackId || "",
                        deliveryStatus: status.status === "failed" ? "bounced" : status.status === "suppressed" ? "bounced" : "pending",
                      }));
                      // Only remove the specific draft that was just sent — keep all other drafts intact
                      const withoutDraft = (values.leadsEmails || []).filter(
                        e => !e.isDraft || !editingDraft || e.id !== editingDraft.id
                      );
                      const combinedEmails = [...withoutDraft, ...newEmails];
                      await setFieldValue("leadsEmails", combinedEmails);
                      await setFieldValue("lastContactedDate", newDate);
                      setEditingDraft(null);
                      setTimeout(() => { submitForm(); setEmailOpen(false); }, 100);
                    }}
                    setEmailOpen={(val) => { setEmailOpen(val); if (!val) setEditingDraft(null); }}
                    EmailList={EmailList}
                  />
                </div>
              )}

              {/* ── Draft cards ──────────────────────────────────────────── */}
              {draftEmails.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 2 }}>
                    Drafts ({draftEmails.length})
                  </div>
                  {draftEmails.map((draft) => (
                    <div key={draft.id || draft.draftSavedAt} style={{
                      display: "flex", alignItems: "center", gap: 12,
                      background: "#fffbeb", border: "1.5px dashed #fbbf24",
                      borderRadius: 10, padding: "10px 14px",
                    }}>
                      {/* Draft badge */}
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <FontAwesomeIcon icon={faEnvelope} style={{ color: "#d97706", fontSize: 14 }} />
                      </div>
                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#92400e", background: "#fde68a", borderRadius: 4, padding: "1px 7px" }}>
                            Draft
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 300 }}>
                            {draft.emailBody || "(No subject)"}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                          {draft.emailedto ? <>To: {draft.emailedto}&nbsp;&nbsp;</> : null}
                          {draft.draftSavedAt ? <>Saved {dayjs(draft.draftSavedAt).fromNow()}</> : "Not yet saved"}
                        </div>
                      </div>
                      {/* Actions */}
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={() => { setEditingDraft(draft); setEmailOpen(true); }}
                          style={{ padding: "4px 12px", borderRadius: 6, border: "1.5px solid #d97706", background: "#fff", color: "#d97706", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            Modal.confirm({
                              title: "Discard draft?",
                              content: "This draft will be permanently deleted.",
                              okText: "Discard", okType: "danger",
                              cancelText: "Cancel", centered: true,
                              onOk: async () => {
                                if (draft.id && SalesInfo?.id) {
                                  try {
                                    const result = await dispatch(discardDraftThunk({ leadId: SalesInfo.id, draftId: draft.id })).unwrap();
                                    setFieldValue("leadsEmails", result?.leadsEmails || (values.leadsEmails || []).filter(e => e.id !== draft.id));
                                  } catch {
                                    setFieldValue("leadsEmails", (values.leadsEmails || []).filter(e => e.id !== draft.id));
                                  }
                                } else {
                                  setFieldValue("leadsEmails", (values.leadsEmails || []).filter(e => e.id !== draft.id));
                                }
                              },
                            });
                          }}
                          style={{ padding: "4px 10px", borderRadius: 6, border: "1.5px solid #ef4444", background: "#fff", color: "#ef4444", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
                        >
                          Discard
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Email cards */}
              {reversed.length === 0 && draftEmails.length === 0 ? (
                <EmptyEmails />
              ) : reversed.length === 0 ? null : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
                  {reversed.map((email, idx) => {
                    const key = email.emailID || idx;
                    const isExpanded = expandedRowKeys.includes(key);
                    const status = email.deliveryStatus || "pending";
                    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
                    const replies = repliesMap[email.emailID] || [];
                    const isLoading = loadingReplies[email.emailID];
                    const sentTime = email.time ? dayjs(email.time) : null;
                    const relTime = sentTime?.isValid() ? sentTime.fromNow() : null;
                    const fullTime = sentTime?.isValid() ? sentTime.format("DD MMM YYYY, hh:mm A") : null;

                    return (
                      <div key={key} style={{
                        borderRadius: 12,
                        border: "1px solid #e8edf5",
                        borderLeft: `4px solid ${cfg.border}`,
                        background: "#fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        overflow: "hidden",
                        transition: "box-shadow 0.2s",
                      }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)"}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)"}
                      >
                        {/* Card top row */}
                        <div style={{ padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                          <Avatar email={email.emailFrom} size={38} />

                          <div style={{ flex: 1, minWidth: 0 }}>
                            {/* From → To */}
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>
                                {shortEmail(email.emailFrom) || "--"}
                              </span>
                              <FontAwesomeIcon icon={faArrowRight} style={{ color: "#94a3b8", fontSize: 9, flexShrink: 0 }} />
                              <span style={{ fontSize: 12, color: "#475569" }}>
                                {shortEmail(email.emailedto) || "--"}
                              </span>
                              {email.emailedy && (
                                <span style={{
                                  marginLeft: 4, fontSize: 10, color: "#94a3b8",
                                  background: "#f1f5f9", borderRadius: 20, padding: "1px 8px",
                                }}>
                                  via {email.emailedy}
                                </span>
                              )}
                            </div>

                            {/* Subject */}
                            <div style={{
                              fontSize: 13, fontWeight: 600, color: "#0f172a",
                              marginTop: 4, overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {email.emailBody || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>No subject</span>}
                            </div>
                          </div>

                          {/* Right side: time + action buttons + badge */}
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                            <Tooltip title={fullTime}>
                              <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", cursor: "default" }}>
                                {relTime || "--"}
                              </span>
                            </Tooltip>
                            {email.bounceReason
                              ? <Tooltip title={email.bounceReason}><span><DeliveryBadge status={status} /></span></Tooltip>
                              : <DeliveryBadge status={status} />
                            }
                          </div>
                        </div>

                        {/* Thread toggle row */}
                        {email.emailID && (
                          <div
                            onClick={() => onExpand(key, email)}
                            style={{
                              padding: "8px 16px",
                              borderTop: "1px solid #f1f5f9",
                              background: isExpanded ? "#f8faff" : "#fafafa",
                              display: "flex", alignItems: "center", gap: 8,
                              cursor: "pointer", userSelect: "none",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f0f4ff"}
                            onMouseLeave={e => e.currentTarget.style.background = isExpanded ? "#f8faff" : "#fafafa"}
                          >
                            <div style={{
                              width: 20, height: 20, borderRadius: 6,
                              background: isExpanded ? "#6366f1" : "#e2e8f0",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              transition: "background 0.15s",
                            }}>
                              <FontAwesomeIcon
                                icon={isLoading ? faClock : isExpanded ? faChevronDown : faChevronRight}
                                style={{ fontSize: 9, color: isExpanded ? "#fff" : "#64748b" }}
                              />
                            </div>
                            <span style={{ fontSize: 12, color: isExpanded ? "#6366f1" : "#64748b", fontWeight: 600 }}>
                              {isLoading ? "Fetching replies…"
                                : isExpanded
                                  ? (() => { const rc = replies.filter(r => r.provider !== 'ses-sent').length; return `Hide thread${rc > 0 ? ` (${rc} repl${rc === 1 ? "y" : "ies"})` : ""}`; })()
                                  : "View thread & replies"}
                            </span>
                            {newReplyIds.has(email.emailID) && !isExpanded && (
                              <span style={{
                                marginLeft: "auto",
                                display: "inline-flex", alignItems: "center", gap: 4,
                                background: "#8b5cf6", color: "#fff",
                                borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 700,
                              }}>
                                <FontAwesomeIcon icon={faReply} style={{ fontSize: 9 }} />
                                New reply
                              </span>
                            )}
                          </div>
                        )}

                        {/* Expanded thread */}
                        {isExpanded && (
                          <div style={{ borderTop: "1px solid #e2e8f0", background: "#f8faff", padding: "0 0 8px 0" }}>
                            {replies.length > 0 ? (
                              <EmailThread EmailList={EmailList} replies={replies} userEmail={email.emailFrom} handleFetchData={fetchReplies} salesInfo={effectiveSalesInfo} originalEmailId={email.emailID} />
                            ) : (
                              <div style={{
                                padding: "20px 20px 16px", textAlign: "center",
                                color: "#94a3b8", fontSize: 13,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
                              }}>
                                <div>No replies found yet.</div>
                                <button
                                  onClick={() => setReplyCompose({
                                    mode: 'reply',
                                    data: {
                                      messageId: email.emailID,
                                      to: email.emailedto || '',
                                      subject: email.emailBody?.startsWith('Re:') ? email.emailBody : `Re: ${email.emailBody || ''}`,
                                      body: '',
                                      from: email.emailFrom || '',
                                      date: email.date,
                                      attachments: [],
                                    }
                                  })}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 7,
                                    padding: '6px 16px', borderRadius: 6,
                                    border: '1px solid #6366f1',
                                    background: '#fff', cursor: 'pointer',
                                    color: '#6366f1', fontSize: 12, fontWeight: 600,
                                    transition: 'all 0.15s',
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                                >
                                  <FontAwesomeIcon icon={faReply} style={{ fontSize: 11 }} />
                                  Send Follow-up
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

            </>
          )}
        />

        {/* Reply/Forward Modal */}
        <Modal
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FontAwesomeIcon
                icon={replyCompose?.mode === 'forward' ? faShare : faReply}
                style={{ color: '#6366f1', fontSize: 14 }}
              />
              {replyCompose?.mode === 'forward' ? 'Forward Email' : 'Reply to Email'}
            </span>
          }
          open={!!replyCompose}
          onCancel={() => setReplyCompose(null)}
          footer={null}
          width="90%"
          zIndex={2000}
          destroyOnClose
        >
          {replyCompose && (
            <EmailForm
              EmailList={EmailList}
              replyData={replyCompose.mode !== 'forward' ? replyCompose.data : null}
              forwardData={replyCompose.mode === 'forward' ? replyCompose.data : null}
              handleCloseModal={() => setReplyCompose(null)}
              salesInfo={effectiveSalesInfo}
            />
          )}
        </Modal>

        {/* Comment field */}
        <div style={{ marginTop: 20 }}>
          <CommentField label="Email Comments" name="emailComments" />
        </div>
      </div>
    </div>
  );
});

export default EmailTrack;
